import React, { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Printer,
  Download,
  ZoomIn,
  ZoomOut,
  FileText,
  Ship,
  Layers,
  Scale,
  ShieldCheck,
  Receipt,
  RotateCcw,
  CheckCircle2,
  Paperclip,
  X
} from 'lucide-react'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import toast from 'react-hot-toast'
import { ExpedienteDespacho } from '../PaginaDespachoPortuario'
import { HojaPedimentoSAT } from './HojaPedimentoSAT'
import { HojaDODASAT } from './HojaDODASAT'
import { HojaManifestacionValor } from './HojaManifestacionValor'
import { HojaBillOfLading } from './HojaBillOfLading'
import { HojaTarjaSSA } from './HojaTarjaSSA'
import { HojaPasePIS } from './HojaPasePIS'
import { HojaCertificadoVGM } from './HojaCertificadoVGM'
import { HojaFacturaComercial } from './HojaFacturaComercial'
import { QrCode, FileSignature } from 'lucide-react'

export type TipoDocumentoFisico = 'PEDIMENTO' | 'DODA' | 'MVE' | 'BL' | 'TARJA' | 'PIS' | 'VGM' | 'FACTURA'

interface Props {
  expediente?: ExpedienteDespacho | null
  todosExpedientes?: ExpedienteDespacho[]
  onSeleccionarExpediente?: (exp: ExpedienteDespacho) => void
  onCerrarModal?: () => void
  documentoInicial?: TipoDocumentoFisico
  esModal?: boolean
}

export const VisorExpedienteFisico: React.FC<Props> = ({
  expediente,
  todosExpedientes = [],
  onSeleccionarExpediente,
  onCerrarModal,
  documentoInicial = 'PEDIMENTO',
  esModal = false
}) => {
  const [docActivo, setDocActivo] = useState<TipoDocumentoFisico>(documentoInicial)
  const [zoom, setZoom] = useState<number>(() => (typeof window !== 'undefined' && window.innerWidth < 640 ? 58 : 95))
  const [mostrarSellos, setMostrarSellos] = useState<boolean>(true)
  const [mostrarClip, setMostrarClip] = useState<boolean>(true)
  const [mostrarPerforaciones, setMostrarPerforaciones] = useState<boolean>(true)
  const [descargandoPDF, setDescargandoPDF] = useState<boolean>(false)

  const hojaPapelRef = useRef<HTMLDivElement>(null)

  // Imprimir documento directamente en impresora física
  const handleImprimir = () => {
    window.print()
  }

  // Descargar PDF de alta resolución
  const handleDescargarPDF = async () => {
    if (!hojaPapelRef.current || !expediente) return
    try {
      setDescargandoPDF(true)
      const toastId = toast.loading('Generando PDF de hoja oficial...')

      const canvas = await (html2canvas as any)(hojaPapelRef.current, {
        scale: 2.2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      })

      const imgData = canvas.toDataURL('image/jpeg', 0.95)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'letter'
      })

      const pdfWidth = 215.9 // Letter width mm
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width

      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight)
      pdf.save(`${docActivo}_${expediente.folio}_${expediente.contenedor.numero}.pdf`)

      toast.success('¡PDF exportado con éxito!', { id: toastId })
    } catch (err) {
      console.error('Error al exportar PDF:', err)
      toast.error('Ocurrió un error al generar el PDF de la hoja')
    } finally {
      setDescargandoPDF(false)
    }
  }

  const catalogDocs = [
    { id: 'PEDIMENTO' as TipoDocumentoFisico, label: 'Pedimento SAT', icono: FileText },
    { id: 'DODA' as TipoDocumentoFisico, label: 'DODA (QR Modulación)', icono: QrCode },
    { id: 'MVE' as TipoDocumentoFisico, label: 'Manifestación MVE', icono: FileSignature },
    { id: 'BL' as TipoDocumentoFisico, label: 'B/L Marítimo', icono: Ship },
    { id: 'TARJA' as TipoDocumentoFisico, label: 'Tarja SSA', icono: Layers },
    { id: 'PIS' as TipoDocumentoFisico, label: 'Pase PIS', icono: ShieldCheck },
    { id: 'VGM' as TipoDocumentoFisico, label: 'VGM SOLAS', icono: Scale },
    { id: 'FACTURA' as TipoDocumentoFisico, label: 'Factura & Packing', icono: Receipt }
  ]

  if (!expediente) {
    return (
      <div className="p-10 rounded-[24px] bg-white/80 dark:bg-[#0c0c0c] border border-slate-200/80 dark:border-white/[0.08] backdrop-blur-xl shadow-xl text-center max-w-xl mx-auto my-8 space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-500 flex items-center justify-center mx-auto">
          <FileText className="w-8 h-8" />
        </div>
        <h3 className="text-xl font-bold text-slate-900 dark:text-white">
          Visor de Expedientes en Hojas Físicas
        </h3>
        <p className="text-sm text-slate-600 dark:text-zinc-400">
          No hay un expediente seleccionado para visualizar en hojas físicas. Selecciona un salón con expedientes o genera una práctica con células para 4-CAS-1.
        </p>
      </div>
    )
  }

  return (
    <div className={`w-full flex flex-col bg-[#0d0e12] rounded-2xl overflow-hidden border border-slate-800 shadow-2xl ${esModal ? 'h-full max-h-[92vh]' : 'min-h-[85vh]'}`}>
      {/* ========================================================================= */}
      {/* BARRA SUPERIOR ULTRA COMPACTA (ALTURA: 52PX - 1 SOLA FILA) */}
      {/* ========================================================================= */}
      <header className="h-14 shrink-0 px-3 sm:px-4 bg-[#13141a]/95 backdrop-blur-md border-b border-slate-800/80 flex items-center justify-between gap-3 z-30 select-none print:hidden">
        {/* LADO IZQUIERDO: Identificador y Selector de Expediente */}
        <div className="flex items-center gap-2.5 shrink-0">
          <div className="h-8 w-8 rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-400 flex items-center justify-center shrink-0">
            <FileText className="h-4 w-4" />
          </div>

          <div className="flex items-center gap-2">
            <span className="font-mono text-xs font-black text-white bg-slate-800/80 px-2 py-0.5 rounded border border-slate-700/60 hidden sm:inline-block">
              {expediente.folio}
            </span>

            {todosExpedientes.length > 1 && onSeleccionarExpediente ? (
              <select
                value={expediente.id}
                onChange={(e) => {
                  const enc = todosExpedientes.find((x) => x.id === e.target.value)
                  if (enc) onSeleccionarExpediente(enc)
                }}
                className="px-2.5 py-1 rounded-lg bg-slate-800/90 border border-slate-700 text-xs font-bold text-slate-200 focus:outline-none focus:ring-1 focus:ring-amber-500/50 max-w-[200px] truncate"
              >
                {todosExpedientes.map((x) => (
                  <option key={x.id} value={x.id} className="bg-slate-900 text-white">
                    {x.folio} - {x.contenedor.numero}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-xs font-bold text-slate-300 truncate max-w-[180px] hidden md:inline-block">
                {expediente.titulo}
              </span>
            )}
          </div>
        </div>

        {/* CENTRO: Pestañas Segmentadas de Documentos (Compactas) */}
        <nav className="flex items-center gap-1 bg-slate-900/90 p-1 rounded-xl border border-slate-800/80 overflow-x-auto no-scrollbar max-w-full">
          {catalogDocs.map((doc) => {
            const Icono = doc.icono
            const esActivo = docActivo === doc.id
            return (
              <button
                key={doc.id}
                type="button"
                onClick={() => setDocActivo(doc.id)}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap shrink-0 ${
                  esActivo
                    ? 'bg-amber-500 text-slate-950 font-black shadow-sm'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
                }`}
              >
                <Icono className={`h-3.5 w-3.5 ${esActivo ? 'text-slate-950' : 'text-slate-400'}`} />
                <span>{doc.label}</span>
              </button>
            )
          })}
        </nav>

        {/* LADO DERECHO: Herramientas (Zoom, Sellos, Clip, Imprimir, PDF, Cerrar) */}
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Zoom Compacto */}
          <div className="flex items-center bg-slate-800/80 rounded-lg p-0.5 border border-slate-700/60">
            <button
              onClick={() => setZoom((z) => Math.max(70, z - 10))}
              title="Reducir zoom"
              className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-all"
            >
              <ZoomOut className="h-3.5 w-3.5" />
            </button>
            <span className="px-1.5 text-[11px] font-mono font-bold text-slate-300 select-none">
              {zoom}%
            </span>
            <button
              onClick={() => setZoom((z) => Math.min(130, z + 10))}
              title="Aumentar zoom"
              className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-all"
            >
              <ZoomIn className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setZoom(95)}
              title="Ajustar encuadre (95%)"
              className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-all"
            >
              <RotateCcw className="h-3 w-3" />
            </button>
          </div>

          {/* Toggle Sellos */}
          <button
            type="button"
            onClick={() => setMostrarSellos(!mostrarSellos)}
            title="Mostrar u ocultar sellos oficiales de goma"
            className={`px-2 py-1 rounded-lg text-xs font-bold border transition-all flex items-center gap-1 hidden sm:flex ${
              mostrarSellos
                ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                : 'bg-slate-800/60 text-slate-400 border-slate-700/60 hover:text-white'
            }`}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span className="text-[11px]">Sellos</span>
          </button>

          {/* Toggle Clip */}
          <button
            type="button"
            onClick={() => {
              setMostrarClip(!mostrarClip)
              setMostrarPerforaciones(!mostrarPerforaciones)
            }}
            title="Activar/Desactivar clip y perforaciones de archivo"
            className={`p-1.5 rounded-lg border transition-all ${
              mostrarClip
                ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                : 'bg-slate-800/60 text-slate-400 border-slate-700/60 hover:text-white'
            }`}
          >
            <Paperclip className="h-3.5 w-3.5" />
          </button>

          {/* Botón Imprimir */}
          <button
            type="button"
            onClick={handleImprimir}
            title="Imprimir hoja física (Ctrl+P)"
            className="p-1.5 rounded-lg bg-slate-800/90 hover:bg-slate-700 text-slate-200 border border-slate-700/60 transition-all"
          >
            <Printer className="h-3.5 w-3.5 text-amber-400" />
          </button>

          {/* Botón Descargar PDF */}
          <button
            type="button"
            onClick={handleDescargarPDF}
            disabled={descargandoPDF}
            title="Descargar hoja física en PDF"
            className="px-2.5 py-1 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-black text-xs transition-all flex items-center gap-1 shadow-sm disabled:opacity-50 active:scale-95"
          >
            <Download className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">PDF</span>
          </button>

          {/* Botón Cerrar (en caso de modal) */}
          {esModal && onCerrarModal && (
            <button
              type="button"
              onClick={onCerrarModal}
              title="Cerrar visor"
              className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all ml-0.5"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </header>

      {/* ========================================================================= */}
      {/* MESA DE TRABAJO (ÁREA PRINCIPAL CON MÁXIMO ENCUADRE Y SCROLL LIMPIO) */}
      {/* ========================================================================= */}
      <main className="flex-1 overflow-auto p-2 sm:p-6 flex items-start justify-center bg-[#090a0e] print:p-0 print:bg-white print:overflow-visible touch-pan-x touch-pan-y">
        {/* Contenedor con Escala de Zoom */}
        <div
          style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top center' }}
          className="transition-transform duration-150 ease-out max-w-[820px] w-full pb-8 print:transform-none print:max-w-none print:w-full print:p-0"
        >
          {/* ========================================================================= */}
          {/* HOJA DE PAPEL FÍSICA */}
          {/* ========================================================================= */}
          <div
            ref={hojaPapelRef}
            className="relative w-full bg-white shadow-[0_20px_50px_rgba(0,0,0,0.5),0_0_1px_rgba(0,0,0,0.2)] rounded-sm transition-all print:shadow-none print:m-0"
          >
            {/* Clip Metálico */}
            {mostrarClip && (
              <div className="absolute -top-2.5 left-7 z-20 pointer-events-none print:hidden">
                <div className="h-8 w-3.5 rounded-full border-2 border-slate-400 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-400 shadow-md transform -rotate-6" />
              </div>
            )}

            {/* Perforaciones de Archivador */}
            {mostrarPerforaciones && (
              <div className="absolute left-2 top-0 bottom-0 flex flex-col justify-around pointer-events-none z-10 print:hidden">
                <div className="h-3.5 w-3.5 rounded-full bg-slate-900 shadow-inner border border-slate-700/60" />
                <div className="h-3.5 w-3.5 rounded-full bg-slate-900 shadow-inner border border-slate-700/60" />
              </div>
            )}

            {/* Renderizado del Documento Físico */}
            <AnimatePresence mode="wait">
              <motion.div
                key={docActivo}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.12 }}
                className="w-full"
              >
                {docActivo === 'PEDIMENTO' && (
                  <HojaPedimentoSAT expediente={expediente} mostrarSelloVerde={mostrarSellos} />
                )}
                {docActivo === 'DODA' && (
                  <HojaDODASAT expediente={expediente} mostrarSelloVerde={mostrarSellos} />
                )}
                {docActivo === 'MVE' && <HojaManifestacionValor expediente={expediente} />}
                {docActivo === 'BL' && (
                  <HojaBillOfLading expediente={expediente} mostrarSelloNaviera={mostrarSellos} />
                )}
                {docActivo === 'TARJA' && <HojaTarjaSSA expediente={expediente} />}
                {docActivo === 'PIS' && <HojaPasePIS expediente={expediente} />}
                {docActivo === 'VGM' && <HojaCertificadoVGM expediente={expediente} />}
                {docActivo === 'FACTURA' && <HojaFacturaComercial expediente={expediente} />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  )
}
